import DOMPurify from 'dompurify';

// Configure DOMPurify for user messages
// Allow NO HTML tags for user input - everything should be escaped
const USER_MESSAGE_CONFIG = {
  ALLOWED_TAGS: [], // No HTML tags allowed
  ALLOWED_ATTR: [], // No attributes allowed
  KEEP_CONTENT: true, // Keep text content
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
};

// For messages that need some markdown formatting preserved
const MARKDOWN_SAFE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'code', 'pre', 'blockquote', 
                 'ul', 'ol', 'li', 'a'],
  ALLOWED_ATTR: ['href', 'title'],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover']
};

/**
 * Sanitizes user input to prevent XSS attacks.
 * Removes all HTML tags but preserves text content.
 * This allows users to type HTML/script examples that will display as text.
 * @param {string} input - The user input to sanitize
 * @returns {string} - Sanitized text safe for display
 */
export const sanitizeUserInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // Additional safety check for edge cases
  if (input === null || input === undefined) return '';
  
  // For user messages, we want to escape ALL HTML but preserve the text
  // This allows users to type things like "<script>" and see it as text
  return DOMPurify.sanitize(input, USER_MESSAGE_CONFIG);
};

/**
 * Alternative HTML entity encoding for display.
 * Converts HTML special characters to their entity equivalents.
 * @param {string} text - The text to escape
 * @returns {string} - Text with HTML entities escaped
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return text.replace(/[&<>"'/]/g, char => map[char]);
};

/**
 * Sanitizes content that may contain markdown.
 * Allows safe markdown tags but removes dangerous HTML.
 * @param {string} content - The markdown content to sanitize
 * @returns {string} - Sanitized markdown safe for rendering
 */
export const sanitizeMarkdown = (content) => {
  if (!content || typeof content !== 'string') return '';
  
  return DOMPurify.sanitize(content, MARKDOWN_SAFE_CONFIG);
};